(function () {
  if (window.__cartUpsellCarouselLoaded) {
    return;
  }

  window.__cartUpsellCarouselLoaded = true;

  function rootPath() {
    var root =
      window.Shopify && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root
        : "/";

    if (root.slice(-1) !== "/") {
      root += "/";
    }

    return root;
  }

  /*============================================================================
    <dy-cart-upsell>
    Lightweight scroll-snap carousel for the cart drawer. The DY campaign
    injects this element with <remote-product-card> children; each card loads
    its markup via section rendering (see dy-remote-product-card.js).
  ==============================================================================*/
  class DyCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.scroller = null;
      this.headingEl = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.isUpdating = false;
      this.observer = null;
      this.onRemoteCardLoaded = this.onRemoteCardLoaded.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onInlineAdd = this.onInlineAdd.bind(this);
    }

    connectedCallback() {
      this.ensureMarkup();
      this.renderTitle();
      this.collectCards();
      this.observeChildren();
      this.addEventListener("remote-product-card:loaded", this.onRemoteCardLoaded);
      this.addEventListener("click", this.onInlineAdd);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      this.removeEventListener(
        "remote-product-card:loaded",
        this.onRemoteCardLoaded
      );
      this.removeEventListener("click", this.onInlineAdd);
    }

    /*==========================================================================
      Inline add-to-cart
      The [data-inline-add] button rendered by product-grid-item has no theme
      handler outside the cart-threshold modal, so the upsell wires its own
      (mirroring cart-threshold-modal.js).
    ==========================================================================*/
    onInlineAdd(event) {
      var btn =
        event.target && event.target.closest
          ? event.target.closest("[data-inline-add]")
          : null;

      if (!btn || btn.disabled || !this.contains(btn)) {
        return;
      }

      event.preventDefault();

      var wrap = btn.closest("[data-inline-atc]");
      var select = wrap ? wrap.querySelector("[data-inline-size]") : null;
      var variantId = select ? select.value : null;

      if (!variantId) {
        this.setInlineError(wrap, "Please select a size");
        return;
      }

      this.setInlineError(wrap, "");
      btn.classList.add("is-loading");
      btn.disabled = true;

      fetch(rootPath() + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((response) =>
          response.json().then((data) => ({ ok: response.ok, data: data }))
        )
        .then((result) => {
          btn.classList.remove("is-loading");
          btn.disabled = false;

          if (!result.ok || (result.data && result.data.status)) {
            var message =
              (result.data &&
                (result.data.description || result.data.message)) ||
              "Could not add to cart";
            this.setInlineError(wrap, message);
            return;
          }

          // On the cart page nothing handles ajaxProduct:added (the theme
          // doesn't instantiate CartDrawer there) — reload like theme.js does.
          if (
            document.body.classList.contains("template-cart") ||
            /\/cart\/?($|\?)/.test(window.location.pathname)
          ) {
            window.scrollTo(0, 0);
            window.location.reload();
            return;
          }

          document.dispatchEvent(
            new CustomEvent("ajaxProduct:added", {
              bubbles: true,
              detail: { product: result.data },
            })
          );
        })
        .catch((error) => {
          btn.classList.remove("is-loading");
          btn.disabled = false;
          this.setInlineError(wrap, "Could not add to cart");
          console.error(error);
        });
    }

    setInlineError(wrap, message) {
      if (!wrap) {
        return;
      }

      var el = wrap.querySelector(".grid-product__inline-error");

      if (!el && message) {
        el = document.createElement("div");
        el.className = "grid-product__inline-error";
        wrap.appendChild(el);
      }

      if (el) {
        el.textContent = message;
      }
    }

    defaultTitle() {
      var target = this.closest("[data-default-title]");
      return target ? target.dataset.defaultTitle : "";
    }

    ensureMarkup() {
      // A re-render (DY replacing innerHTML after init) wipes the heading and
      // scroller while leaving the cached references pointing at detached
      // nodes — cards would then be moved into a container that is no longer
      // in the document and the carousel would silently render empty.
      if (this.scroller && !this.contains(this.scroller)) {
        this.scroller = null;
        this.headingEl = null;
        this.prevBtn = null;
        this.nextBtn = null;
      }

      if (this.scroller) {
        return;
      }

      this.isUpdating = true;

      var heading = document.createElement("p");
      heading.className = "cart-upsell__heading";

      var wrapper = document.createElement("div");
      wrapper.className = "cart-upsell__wrapper";

      var scroller = document.createElement("div");
      scroller.className = "cart-upsell__scroller";

      var prevBtn = this.buildArrow("prev");
      var nextBtn = this.buildArrow("next");

      wrapper.appendChild(prevBtn);
      wrapper.appendChild(scroller);
      wrapper.appendChild(nextBtn);

      this.insertBefore(wrapper, this.firstChild);
      this.insertBefore(heading, wrapper);

      this.headingEl = heading;
      this.scroller = scroller;
      this.prevBtn = prevBtn;
      this.nextBtn = nextBtn;

      scroller.addEventListener("scroll", this.onScroll, { passive: true });

      this.isUpdating = false;
    }

    buildArrow(direction) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "cart-upsell__arrow cart-upsell__arrow--" + direction;
      btn.setAttribute(
        "aria-label",
        direction === "prev" ? "Previous products" : "Next products"
      );
      btn.innerHTML =
        direction === "prev"
          ? '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>'
          : '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';

      btn.addEventListener("click", () => {
        var amount = Math.max(this.scroller.clientWidth * 0.8, 120);
        this.scroller.scrollBy({
          left: direction === "prev" ? -amount : amount,
          behavior: "smooth",
        });
      });

      return btn;
    }

    renderTitle() {
      if (!this.headingEl) {
        return;
      }

      var title = (this.getAttribute("title") || "").trim() || this.defaultTitle();

      this.headingEl.textContent = title;
      this.headingEl.hidden = !title;
    }

    collectCards() {
      this.ensureMarkup();

      if (!this.scroller) {
        return;
      }

      var cards = Array.from(this.querySelectorAll("remote-product-card")).filter(
        (card) => card.parentElement !== this.scroller
      );

      if (!cards.length) {
        // Cards already in place may have rendered from the in-memory markup
        // cache, which resolves synchronously and can beat this element's
        // load listener. Reveal whatever is present regardless.
        this.revealAllCardImages();
        this.updateVisibility();
        return;
      }

      this.isUpdating = true;

      cards.forEach((card) => {
        this.scroller.appendChild(card);
      });

      this.isUpdating = false;
      this.revealAllCardImages();
      this.updateVisibility();
    }

    revealAllCardImages() {
      this.querySelectorAll("remote-product-card").forEach((card) => {
        if (card.children.length) {
          this.revealCardImages(card);
        }
      });
    }

    observeChildren() {
      if (this.observer) {
        return;
      }

      this.observer = new MutationObserver((mutations) => {
        if (this.isUpdating) {
          return;
        }

        var hasCardChange = mutations.some((mutation) => {
          var nodes = Array.from(mutation.addedNodes).concat(
            Array.from(mutation.removedNodes)
          );

          return nodes.some((node) => {
            if (node.nodeType !== 1) {
              return false;
            }

            if (node.matches && node.matches("remote-product-card")) {
              return true;
            }

            return Boolean(
              node.querySelector && node.querySelector("remote-product-card")
            );
          });
        });

        if (hasCardChange) {
          this.collectCards();
        }
      });

      this.observer.observe(this, { childList: true });
    }

    onRemoteCardLoaded(event) {
      var card =
        event.target && event.target.closest
          ? event.target.closest("remote-product-card")
          : null;

      if (card) {
        this.hoistQuickShopModals(card);
        this.revealCardImages(card);
      }

      if (window.theme && typeof theme.reinitProductGridItem === "function") {
        theme.reinitProductGridItem(this);
      }

      this.updateVisibility();
    }

    // Cards are built for normal document flow: an AOS scroll-reveal on the
    // image, a shimmer placeholder, and lazy loading. None of that works
    // inside the fixed, internally-scrolled drawer, where AOS never fires and
    // the image box stays empty until a reload. Strip the scroll-reveal out
    // of these cards entirely and settle the other two here.
    revealCardImages(card) {
      card.querySelectorAll("[data-aos]").forEach((el) => {
        el.removeAttribute("data-aos");
        el.removeAttribute("data-aos-offset");
        el.classList.remove("aos-init", "aos-animate");
      });

      card.querySelectorAll(".image-wrap").forEach((el) => {
        el.classList.add("loaded");
      });

      card.querySelectorAll("img[loading='lazy']").forEach((img) => {
        img.setAttribute("loading", "eager");

        // Re-assign src so a deferred lazy request starts immediately.
        var src = img.getAttribute("src");
        if (src && !img.complete) {
          img.setAttribute("src", src);
        }
      });
    }

    onScroll() {
      this.updateArrows();
    }

    updateVisibility() {
      var count = this.scroller
        ? this.scroller.querySelectorAll("remote-product-card").length
        : 0;

      this.classList.toggle("is-empty", count === 0);
      this.updateArrows();
    }

    updateArrows() {
      if (!this.scroller || !this.prevBtn || !this.nextBtn) {
        return;
      }

      var maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      var scrollable = maxScroll > 4;

      this.classList.toggle("has-overflow", scrollable);
      this.prevBtn.disabled = this.scroller.scrollLeft <= 4;
      this.nextBtn.disabled = this.scroller.scrollLeft >= maxScroll - 4;
    }

    hoistQuickShopModals(scope) {
      if (!scope || !document.body) {
        return;
      }

      var modals = Array.from(scope.querySelectorAll(".modal--quick-shop[id]"));

      if (!modals.length) {
        return;
      }

      var hoisted = window.__dyQuickShopModalStore;

      if (!hoisted) {
        hoisted = window.__dyQuickShopModalStore = new Map();
      }

      modals.forEach((modal) => {
        var existing = hoisted.get(modal.id);

        if (existing && existing !== modal) {
          modal.remove();
          return;
        }

        if (modal.dataset.dyHoisted === "true") {
          hoisted.set(modal.id, modal);
          return;
        }

        modal.dataset.dyHoisted = "true";
        document.body.appendChild(modal);
        hoisted.set(modal.id, modal);
      });
    }
  }

  if (!customElements.get("dy-cart-upsell")) {
    customElements.define("dy-cart-upsell", DyCartUpsell);
  }

  /*============================================================================
    Lazy campaign init
    DYO.smartObject() is only called once the shopper first opens the cart
    drawer (or adds a product, which opens it), so closed-drawer pageviews
    don't spend requests on upsell product cards.
  ==============================================================================*/
  var lastRenderAt = 0;

  function readConfig() {
    var wrapper = document.querySelector("[data-cart-upsell]");

    if (!wrapper) {
      return null;
    }

    var configNode = wrapper.querySelector("[data-cart-upsell-config]");

    if (!configNode) {
      return null;
    }

    var config = {};

    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      return null;
    }

    if (!config.campaign || !config.target) {
      return null;
    }

    config.wrapper = wrapper;
    return config;
  }

  function initCampaign() {
    var config = readConfig();

    if (!config) {
      return true;
    }

    if (config.wrapper.dataset.dyInitialized === "true") {
      return true;
    }

    if (!window.DYO || typeof window.DYO.smartObject !== "function") {
      return false;
    }

    config.wrapper.dataset.dyInitialized = "true";
    lastRenderAt = Date.now();

    window.DYO.smartObject(config.campaign, {
      target: config.target,
      inline: true,
    });

    return true;
  }

  /*============================================================================
    Re-render on cart changes
    Recommendations are cart-seeded, so after any add/quantity change the
    widget is wiped and re-requested — the just-added product falls out of
    the recommendations on DY's side.
  ==============================================================================*/
  var refreshTimer = null;

  function refreshCampaign() {
    var config = readConfig();

    if (
      !config ||
      config.wrapper.dataset.dyInitialized !== "true" ||
      !window.DYO ||
      typeof window.DYO.smartObject !== "function"
    ) {
      return;
    }

    // Skip when the initial render just happened (first add opens the drawer
    // and triggers init — no point re-rendering the same recs immediately).
    if (Date.now() - lastRenderAt < 1500) {
      return;
    }

    var target = document.getElementById(config.target);

    if (target) {
      target.innerHTML = "";
    }

    lastRenderAt = Date.now();

    window.DYO.smartObject(config.campaign, {
      target: config.target,
      inline: true,
    });
  }

  function scheduleRefresh() {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(function () {
      refreshTimer = null;
      refreshCampaign();
    }, 800);
  }

  document.addEventListener("ajaxProduct:added", scheduleRefresh);
  document.addEventListener("cart:updated", scheduleRefresh);

  function initCampaignWithRetry() {
    if (initCampaign()) {
      return;
    }

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;

      if (initCampaign() || attempts > 50) {
        window.clearInterval(timer);
      }
    }, 200);
  }

  var triggered = false;

  function onFirstDrawerOpen() {
    if (triggered) {
      return;
    }

    triggered = true;

    if (classObserver) {
      classObserver.disconnect();
      classObserver = null;
    }

    document.removeEventListener("ajaxProduct:added", onFirstDrawerOpen);
    document.removeEventListener("cart:open", onFirstDrawerOpen);

    initCampaignWithRetry();
  }

  var classObserver = null;

  function cartDrawerIsOpen() {
    var drawer = document.getElementById("CartDrawer");
    return Boolean(drawer && drawer.classList.contains("drawer--is-open"));
  }

  function watchDrawer() {
    var drawer = document.getElementById("CartDrawer");

    if (!drawer) {
      return;
    }

    if (cartDrawerIsOpen()) {
      onFirstDrawerOpen();
      return;
    }

    classObserver = new MutationObserver(function () {
      if (cartDrawerIsOpen()) {
        onFirstDrawerOpen();
      }
    });

    classObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  document.addEventListener("ajaxProduct:added", onFirstDrawerOpen);
  document.addEventListener("cart:open", onFirstDrawerOpen);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watchDrawer);
  } else {
    watchDrawer();
  }
})();
